import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	Modal,
	MultiSelect,
	NumberInput,
	Stack,
	Switch,
	Text,
	TextInput,
} from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	emptyHotelPromoValues,
	type HotelPromoFormValues,
	hotelPromoSchema,
} from "@/features/hotel-promos/hotel-promo-schema";
import type { HotelOption, HotelPromo } from "@/features/hotel-promos/types";
import { GuestUserPicker } from "@/features/users/ui/guest-user-picker";

export type HotelPromoEditorState =
	| { mode: "create" }
	| { mode: "edit"; row: HotelPromo };

type Option = { value: string; label: string };

export function defaultsFor(
	editor: HotelPromoEditorState,
): HotelPromoFormValues {
	if (editor.mode === "edit") {
		const row = editor.row;
		return {
			code: row.code,
			percent: row.percent,
			isActive: row.isActive,
			partnerIds: row.partnerIds,
			categoryIds: row.categoryIds,
			guests: row.users,
			hotelKeys: row.hotelKeys,
		};
	}
	return emptyHotelPromoValues();
}

export function HotelPromoEditorModal({
	editor,
	saving,
	hotels,
	partners,
	categories,
	hotelsError,
	onClose,
	onSave,
}: {
	editor: HotelPromoEditorState | null;
	saving: boolean;
	hotels: HotelOption[];
	partners: Option[];
	categories: Option[];
	hotelsError?: string;
	onClose: () => void;
	onSave: (values: HotelPromoFormValues) => void;
}) {
	const { t } = useTranslation();
	return (
		<Modal
			opened={editor !== null}
			onClose={onClose}
			title={
				editor?.mode === "edit" ? t("hotel.editTitle") : t("hotel.addTitle")
			}
			centered
			size="lg"
		>
			{editor ? (
				<HotelPromoForm
					key={`${editor.mode}-${editor.mode === "edit" ? editor.row.id : "new"}`}
					editor={editor}
					saving={saving}
					hotels={hotels}
					partners={partners}
					categories={categories}
					hotelsError={hotelsError}
					onClose={onClose}
					onSave={onSave}
				/>
			) : null}
		</Modal>
	);
}

function HotelPromoForm({
	editor,
	saving,
	hotels,
	partners,
	categories,
	hotelsError,
	onClose,
	onSave,
}: {
	editor: HotelPromoEditorState;
	saving: boolean;
	hotels: HotelOption[];
	partners: Option[];
	categories: Option[];
	hotelsError?: string;
	onClose: () => void;
	onSave: (values: HotelPromoFormValues) => void;
}) {
	const { t } = useTranslation();
	const form = useForm<HotelPromoFormValues>({
		resolver: zodResolver(hotelPromoSchema(editor.mode)),
		defaultValues: defaultsFor(editor),
	});
	const guests = form.watch("guests");
	const hotelOptions = hotels.map((hotel) => ({
		value: hotel.key,
		label: hotel.title,
	}));

	return (
		<form onSubmit={form.handleSubmit(onSave)} noValidate>
			<Stack>
				<TextInput
					label={t("hotel.servioCode")}
					description={t("hotel.codeHint")}
					error={form.formState.errors.code?.message}
					{...form.register("code")}
				/>
				<Controller
					name="percent"
					control={form.control}
					render={({ field }) => (
						<NumberInput
							label={t("hotel.percent")}
							description={t("hotel.percentHint")}
							min={1}
							max={100}
							allowDecimal={false}
							value={field.value}
							error={form.formState.errors.percent?.message}
							onChange={(value) => {
								field.onChange(typeof value === "number" ? value : undefined);
							}}
						/>
					)}
				/>
				<Controller
					name="hotelKeys"
					control={form.control}
					render={({ field }) => (
						<MultiSelect
							label={t("hotel.hotels")}
							description={hotelsError ?? t("hotel.hotelsHint")}
							data={hotelOptions}
							value={field.value}
							searchable
							error={form.formState.errors.hotelKeys?.message}
							onChange={field.onChange}
						/>
					)}
				/>
				{hotelsError ? (
					<Text c="red" size="sm">
						{hotelsError}
					</Text>
				) : null}
				<Controller
					name="isActive"
					control={form.control}
					render={({ field }) => (
						<Switch
							label={t("hotel.active")}
							description={t("hotel.activeHint")}
							checked={field.value}
							onChange={(event) => field.onChange(event.currentTarget.checked)}
						/>
					)}
				/>
				<Controller
					name="categoryIds"
					control={form.control}
					render={({ field }) => (
						<MultiSelect
							label={t("hotel.discountCategory")}
							description={t("hotel.categoryHint")}
							data={categories}
							value={field.value}
							searchable
							error={form.formState.errors.categoryIds?.message}
							onChange={field.onChange}
						/>
					)}
				/>
				<GuestUserPicker
					value=""
					label={t("hotel.userField")}
					description={t("hotel.userHint")}
					onChange={() => undefined}
					onPick={(guest) => {
						if (guests.some((item) => item.id === guest.id)) {
							return;
						}
						form.setValue(
							"guests",
							[...guests, { id: guest.id, label: guest.label }],
							{ shouldValidate: true },
						);
					}}
				/>
				{guests.length ? (
					<Group gap="xs">
						{guests.map((guest) => (
							<Button
								key={guest.id}
								size="compact-sm"
								variant="light"
								onClick={() =>
									form.setValue(
										"guests",
										guests.filter((item) => item.id !== guest.id),
										{ shouldValidate: true },
									)
								}
							>
								{guest.label}
							</Button>
						))}
					</Group>
				) : null}
				<Controller
					name="partnerIds"
					control={form.control}
					render={({ field }) => (
						<MultiSelect
							label={t("hotel.domains")}
							description={t("hotel.partnerHint")}
							data={partners}
							value={field.value}
							searchable
							error={form.formState.errors.partnerIds?.message}
							onChange={field.onChange}
						/>
					)}
				/>
				<Group justify="flex-end">
					<Button variant="default" onClick={onClose} disabled={saving}>
						{t("common.cancel")}
					</Button>
					<Button type="submit" loading={saving}>
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
